// SegmentedControl — small pill toggle (Tree/Chart, Web/Mobile, People/Groups…).
// Options may carry an optional leading icon and a trailing count.
// `variant` picks the frame: 'track' (default) is the sunken bordered group;
// 'plain' drops the track so the tabs read as bare pills on the card surface,
// tinting only the active one (opt-in — existing call sites keep 'track').

import Icon from './Icon';

export interface SegmentedOption {
  value: string;
  /** Omit for an icon-only segment — then `icon` and `ariaLabel` are what name
   *  it, since a button with no text content has no accessible name. */
  label?: string;
  /** Kebab-case Icon name shown before the label. */
  icon?: string;
  /** Accessible name when `label` is omitted. Ignored otherwise. */
  ariaLabel?: string;
  /** Small count after the label. */
  count?: number;
}

export type SegmentedVariant = 'track' | 'plain';

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  /** Frame around the group. Default 'track'. */
  variant?: SegmentedVariant;
  /** Opt-in: greys the whole group and disables every segment (e.g. a
   *  setting that only applies while its parent switch is on). */
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

const SegmentedControl = ({
  options,
  value,
  onChange,
  variant = 'track',
  disabled = false,
  className = '',
  ...rest
}: SegmentedControlProps) => (
  <div
    className={[
      'segmented',
      variant === 'plain' && 'segmented--plain',
      disabled && 'segmented--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    role='tablist'
    aria-label={rest['aria-label']}
    aria-disabled={disabled || undefined}
  >
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          type='button'
          role='tab'
          aria-selected={active}
          disabled={disabled}
          className={`segmented__btn${active ? ' segmented__btn--active' : ''}${
            opt.label === undefined ? ' segmented__btn--icon' : ''
          }`}
          aria-label={opt.label === undefined ? opt.ariaLabel : undefined}
          title={opt.label === undefined ? opt.ariaLabel : undefined}
          onClick={() => onChange(opt.value)}
        >
          {opt.icon && <Icon name={opt.icon} size={15} />}
          {opt.label}
          {opt.count !== undefined && opt.count > 0 && (
            <span className='segmented__count'>{opt.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

export default SegmentedControl;
